'use client';

import { sendGAEvent } from '@next/third-parties/google';
import { useTranslations } from 'next-intl';
import { useEffect, useRef } from 'react';
import { useStore } from '@/app/providers/store';
import { reportEvent } from '@/app/utils';
import { safeStorage } from '@/app/utils/client';
import { usePathname } from '@/i18n/routing';
import { getTodayDate } from './use-alert-pricing';
import { useGlobalModal } from './use-global-modal';

const LOW_CREDITS_ALERT_KEY = 'VIDU_LOW_CREDITS_ALERT';

/** 免费用户「积分不足」提醒的积分阈值，低于该值触发提示 */
export const LOW_CREDITS_THRESHOLD = 20;

/**
 * 消费「进入创作 tab 低积分」提示当日名额：当日尚未提示过则标记为已提示并返回 true，否则返回 false。
 * 仅供 useLowCreditsAlert 使用。
 */
export const consumeLowCreditsAlertToday = (): boolean => {
  const today = getTodayDate();
  if (safeStorage.getItem(LOW_CREDITS_ALERT_KEY) === today) return false;
  safeStorage.setItem(LOW_CREDITS_ALERT_KEY, today);
  return true;
};

/**
 * 免费用户进入创作 tab（切换路由）那一刻若积分不足 20，每自然日提示一次升级 toast。
 * 原先此处直接弹订阅弹窗，因打断创作连贯性改为 toast，用户可通过 toast 内按钮进入套餐弹窗。
 * 仅以「路由切换」为触发时机：同一路由上 creditsInfo 变化（如提交扣分）不再重新判定，
 * 但切换到另一个创作工具路由时会再次判定。创作过程中的积分不足由提交路径的
 * InsufficientCredits 弹窗覆盖。
 * 展示动作由调用方注入：hooks 层不能引用 components/shared（分层约束），
 * 且这样 hook 只负责触发判定，提示形态可换。
 * @param onAlert 需要提示时调用，参数为提示文案与操作按钮文案
 * @param enabled 是否启用（默认 true）；工具聚合页等非创作页应传 false 以跳过提示
 */
export const useLowCreditsAlert = (
  onAlert: (options: { message: string; actionText: string; onAction: () => void }) => void,
  enabled = true,
) => {
  const { open: openPlanDetailDialog } = useGlobalModal('planDetailDialog');
  const t = useTranslations();
  const userInfo = useStore((state) => state.userInfo);
  const creditsInfo = useStore((state) => state.creditsInfo);
  const pathname = usePathname();
  const evaluatedPathRef = useRef<string | null>(null);

  useEffect(() => {
    if (!enabled) return;
    if (evaluatedPathRef.current === pathname) return;
    const isFree = userInfo?.subs_plan === 'free';
    if (!isFree || !creditsInfo) return;
    evaluatedPathRef.current = pathname;
    if (creditsInfo.total >= LOW_CREDITS_THRESHOLD) return;

    setTimeout(() => {
      if (consumeLowCreditsAlertToday()) {
        onAlert({
          message: t('payments.plans.toastLowCredits'),
          actionText: t('payments.plans.upgradeNow'),
          onAction: () => openPlanDetailDialog({ fromPage: 'low_credits' }),
        });
        reportEvent('vip_price_toast_exposure', { from_page: 'low_credits' });
        sendGAEvent('event', 'subscription_exposure', { from_page: 'low_credits' });
      }
    }, 0);
  }, [enabled, pathname, userInfo, creditsInfo, openPlanDetailDialog, t, onAlert]);
};
